import { forEach } from 'lodash';
import { apiClient, apiErrorHandler, ADMIN_URL } from 'src/app/common/network';
import { getDayStart, getDayEnd } from 'src/app/common/utils';
import { PaginateListMore } from '../../../common/types/common-types';
import { Dispatch } from 'react';
import { RulesStatus, RulesDetail, RulesListItem, RulesType, RulesDistributionStatus, RulesLeadListItem } from '../types/auto-lead-reassignment-types';

const autoLeadReassignmentEndpoint = 'auto-lead-reassignment';

export type ListParam = {
  ruleId?: string;
  campaignName?: string;
  status?: RulesStatus;
  publishedAtFrom?: Date | null;
  publishedAtTo?: Date | null;
  page: number;
  limit: number;
};

export type LeadListParam = {
  alias?: string;
  agentCodeFrom?: string;
  agentCodeTo?: string;
  status?: RulesDistributionStatus;
  leadDateFrom?: Date | null;
  leadDateTo?: Date | null;
  page: number;
  limit: number;
};

export const fetchList = async (
  ruleType: RulesType,
  params: ListParam,
  dispatch?: Dispatch<any>,
): Promise<PaginateListMore<RulesListItem>> => {
  let queryPath = `${ADMIN_URL}/${autoLeadReassignmentEndpoint}/${String(ruleType)}/list?`;
  forEach(params, (param, key) => {
    if (typeof param === 'object') {
      if (param) {
        if (key.toLowerCase().includes('from')) {
          queryPath += `${key}=${encodeURIComponent(getDayStart(param).toISOString())}&`;
        }
        if (key.toLowerCase().includes('to')) {
          queryPath += `${key}=${encodeURIComponent(getDayEnd(param).toISOString())}&`;
        }
      }
    } else {
      if (key === 'page') {
        queryPath += `${key}=${encodeURIComponent(param as any)}&`;
      } else {
        queryPath += param ? `${key}=${encodeURIComponent(param as any)}&` : '';
      }
    }
  });
  return apiClient
    .get<PaginateListMore<RulesListItem>>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchLeadList = async (
  ruleType: RulesType,
  id: string,
  params: LeadListParam,
  dispatch?: Dispatch<any>,
): Promise<PaginateListMore<RulesLeadListItem>> => {
  let queryPath = `${ADMIN_URL}/${autoLeadReassignmentEndpoint}/${String(ruleType)}/${id}/lead-list?`;
  forEach(params, (param, key) => {
    if (typeof param === 'object') {
      if (param) {
        if (key.toLowerCase().includes('datefrom')) {
          queryPath += `${key}=${encodeURIComponent(getDayStart(param).toISOString())}&`;
        }
        if (key.toLowerCase().includes('dateto')) {
          queryPath += `${key}=${encodeURIComponent(getDayEnd(param).toISOString())}&`;
        }
      }
    } else {
      if (key === 'page') {
        queryPath += `${key}=${encodeURIComponent(param as any)}&`;
      } else {
        queryPath += param ? `${key}=${encodeURIComponent(param as any)}&` : '';
      }
    }
  });
  return apiClient
    .get<PaginateListMore<RulesLeadListItem>>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const create = async (
  ruleType: RulesType,
  body: RulesDetail,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  return apiClient
    .post<void>(`${ADMIN_URL}/${autoLeadReassignmentEndpoint}/${String(ruleType)}/create`, body)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const checkLeadsBeforeCreate = async (
  ruleType: RulesType,
  body: RulesDetail,
  dispatch?: Dispatch<any>,
): Promise<any> => {
  return apiClient
    .post<void>(`${ADMIN_URL}/${autoLeadReassignmentEndpoint}/${String(ruleType)}/check-leads-before-create`, body)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const checkLeadsBeforePublish = async (
  ruleType: RulesType,
  id: string,
  dispatch?: Dispatch<any>,
): Promise<any> => {
  return apiClient
    .get<void>(`${ADMIN_URL}/${autoLeadReassignmentEndpoint}/${String(ruleType)}/${id}/check-leads-before-publish`)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const updateStatus = async (
  ruleType: RulesType,
  id: string,
  status: string,
  dispatch?: Dispatch<any>,
): Promise<void> => {
  return apiClient
    .put<void>(`${ADMIN_URL}/${autoLeadReassignmentEndpoint}/${String(ruleType)}/${id}/update-status`, { status })
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const fetchItem = async (
  ruleType: RulesType,
  id: string,
  dispatch?: Dispatch<any>
): Promise<RulesDetail> => {
  let queryPath = `${ADMIN_URL}/${autoLeadReassignmentEndpoint}/${String(ruleType)}/${id}`;

  return apiClient
    .get<RulesDetail>(queryPath)
    .then((response) => response.data)
    .catch((err) => {
      throw apiErrorHandler(err, dispatch);
    });
};

export const exportLeadListDownload = async (
  ruleType: RulesType,
  id: string,
  params: LeadListParam,
  dispatch?: Dispatch<any>,
) => {
  let queryPath = `${ADMIN_URL}/${autoLeadReassignmentEndpoint}/${String(ruleType)}/${id}/lead-list/export?`;
  forEach(params, (param, key) => {
    if (typeof param === 'object') {
      if (param) {
        if (key.toLowerCase().includes('datefrom')) {
          queryPath += `${key}=${encodeURIComponent(getDayStart(param).toISOString())}&`;
        }
        if (key.toLowerCase().includes('dateto')) {
          queryPath += `${key}=${encodeURIComponent(getDayEnd(param).toISOString())}&`;
        }
      }
    } else {
      if (key === 'page') {
        queryPath += `${key}=${encodeURIComponent(param as any)}&`;
      } else {
        queryPath += param ? `${key}=${encodeURIComponent(param as any)}&` : '';
      }
    }
  });

  apiClient
    .get(queryPath)
    .then((response: any) => {
      const url = window.URL.createObjectURL(
        new Blob(['\uFEFF' + response.data], {
          type: 'text/csv;charset=utf-8;',
        }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${ruleType}_${id}_` + new Date().toISOString() + '.csv');
      document.body.appendChild(link);
      link.click();
    })
    .catch((err: any) => {
      throw apiErrorHandler(err, dispatch);
    });
};